import React, { useState, useEffect } from 'react';
import {
  User,
  Bell,
  Globe,
  Code,
  Copy,
  Check,
  Building2,
  Lock,
  Eye,
  EyeOff,
  Save,
  Upload,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'security' | 'system' | 'widget' | 'lead_sms'>('profile');
  const [showPassword, setShowPassword] = useState(false);
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(user?.avatar || null);
  const [branchInfo, setBranchInfo] = useState<{ name: string; city: string } | null>(null);

  // ... existing code ...

  const settingsTabs = [
    { id: 'profile', name: 'Profil', icon: User },
    { id: 'security', name: 'Güvenlik', icon: Lock },
    ...(user?.unitType === 'sales' ? [
      { id: 'widget', name: 'Form Widget', icon: Code },
      { id: 'lead_sms', name: 'Lead SMS', icon: MessageSquare }
    ] : [])
  ];

  const [copied, setCopied] = useState(false);
  const widgetUrl = `${import.meta.env.VITE_APP_URL || window.location.origin}/form-widget.html`;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const iframeCode = `<iframe
  src="${widgetUrl}"
  width="100%"
  height="850"
  frameborder="0"
  style="border: none; border-radius: 12px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);"
  title="İletişim Formu">
</iframe>`;

  // ... existing code ...




  useEffect(() => {
    const fetchUserBranch = async () => {
      if (!user?.id) return;

      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select(`
            branch_id,
            branches (
              name,
              city
            )
          `)
          .eq('id', user.id)
          .maybeSingle();

        if (profileError) {
          console.error('Profile fetch error:', profileError);
          return;
        }

        if (profile?.branches) {
          const branchData = Array.isArray(profile.branches) ? profile.branches[0] : profile.branches;
          if (branchData) {
            setBranchInfo({
              name: branchData.name,
              city: branchData.city
            });
          }
        }
      } catch (err) {
        console.error('Unexpected error fetching branch:', err);
      }
    };

    fetchUserBranch();
  }, [user?.id]);



  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Dosya boyutu kontrolü (2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('Dosya boyutu 2MB\'dan büyük olamaz');
        return;
      }

      // Dosya formatı kontrolü
      if (!file.type.startsWith('image/')) {
        alert('Sadece resim dosyaları yüklenebilir');
        return;
      }

      setSelectedFile(file);

      // Önizleme için URL oluştur
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    try {
      if (!user?.id) {
        alert('Kullanıcı bilgisi bulunamadı!');
        return;
      }

      updateUser({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        avatar: previewUrl || undefined
      });

      alert('Profil bilgileri başarıyla güncellendi!');
    } catch (err) {
      console.error('Unexpected error saving profile:', err);
      alert('Profil güncellenirken beklenmeyen bir hata oluştu!');
    }
  };

  const handlePasswordChange = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      alert('Yeni şifreler eşleşmiyor!');
      return;
    }

    if (formData.newPassword.length < 6) {
      alert('Yeni şifre en az 6 karakter olmalıdır!');
      return;
    }

    try {
      const { data, error } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (error) throw error;

      alert('Şifreniz başarıyla güncellendi!');

      // Form temizle
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (err: any) {
      console.error('Password update error:', err);
      alert('Şifre güncellenirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    }
  };

  const renderProfileSettings = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Profil Bilgileri</h3>

        <div className="flex items-center space-x-6 mb-6">
          <div className="relative">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Profil fotoğrafı"
                className="w-20 h-20 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700"
              />
            ) : (
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <span className="text-white text-2xl font-semibold">
                  {user?.name.split(' ').map(n => n[0]).join('') || 'U'}
                </span>
              </div>
            )}
          </div>
          <div>
            <input
              type="file"
              id="avatar-upload"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => document.getElementById('avatar-upload')?.click()}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span>Fotoğraf Yükle</span>
            </button>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">JPG, PNG veya GIF. Maksimum 2MB.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ad Soyad
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Adresi
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Telefon
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Şube
            </label>
            <div className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white flex items-center space-x-2">
              <Building2 className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <span>
                {branchInfo ? `${branchInfo.name} (${branchInfo.city})` : 'Şube bilgisi yükleniyor...'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={handleSaveProfile}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
            <span>Kaydet</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bildirim Tercihleri</h3>

        <div className="space-y-4">
          {[
            { id: 'email_notifications', label: 'Email Bildirimleri', description: 'Önemli güncellemeler için email al' },
            { id: 'sms_notifications', label: 'SMS Bildirimleri', description: 'Acil durumlar için SMS al' },
            { id: 'push_notifications', label: 'Push Bildirimleri', description: 'Tarayıcı bildirimleri' },
            { id: 'student_updates', label: 'Öğrenci Güncellemeleri', description: 'Yeni öğrenci kayıtları ve güncellemeler' },
            { id: 'payment_alerts', label: 'Ödeme Uyarıları', description: 'Ödeme alındığında veya geciktiğinde bildir' },
            { id: 'deadline_reminders', label: 'Deadline Hatırlatmaları', description: 'Başvuru deadlineları yaklaştığında hatırlat' }
          ].map((setting) => (
            <div key={setting.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">{setting.label}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">{setting.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" defaultChecked />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Şifre Değiştir</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Mevcut Şifre
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                className="w-full px-4 py-3 pr-12 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Yeni Şifre
            </label>
            <input
              type="password"
              value={formData.newPassword}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Yeni Şifre (Tekrar)
            </label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={handlePasswordChange}
            disabled={!formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-colors ${!formData.currentPassword || !formData.newPassword || !formData.confirmPassword
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
          >
            <Lock className="w-4 h-4" />
            <span>Şifreyi Güncelle</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Removed duplicate variables
  const renderWidgetSettings = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Form Widget Entegrasyonu</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Website'nize kolay entegre edebileceğiniz hazır bir iletişim formu widget'ı. Form gönderimlerini "Website Dataları" sayfasından takip edebilirsiniz.
        </p>

        <div className="space-y-6">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start space-x-3">
              <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-300">Widget URL</h4>
                <p className="text-sm text-blue-800 dark:text-blue-400 mt-1 break-all">
                  {widgetUrl}
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900 dark:text-white">Iframe Kodu</h4>
              <button
                onClick={() => copyToClipboard(iframeCode)}
                className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Kopyalandı!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Kopyala</span>
                  </>
                )}
              </button>
            </div>
            <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
              <code>{iframeCode}</code>
            </pre>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Kullanım Adımları</h4>
            <ol className="space-y-3">
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                  1
                </div>
                <div>
                  <p className="text-gray-900 dark:text-white font-medium">Widget kodunu kopyalayın</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Yukarıdaki iframe kodunu kopyalamak için "Kopyala" butonuna tıklayın.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                  2
                </div>
                <div>
                  <p className="text-gray-900 dark:text-white font-medium">Website'nize ekleyin</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Kopyaladığınız kodu website'nizin HTML dosyasına, formun görünmesini istediğiniz yere yapıştırın.</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                  3
                </div>
                <div>
                  <p className="text-gray-900 dark:text-white font-medium">Form gönderimlerini takip edin</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">"Website Dataları" sayfasından tüm form gönderimlerini görüntüleyebilir ve yönetebilirsiniz.</p>
                </div>
              </li>
            </ol>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Canlı Önizleme</h4>
            <div className="border-2 border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
              <iframe
                src={widgetUrl}
                width="100%"
                height="850"
                style={{ border: 'none' }}
                title="Form Widget Önizleme"
              />
            </div>
          </div>

          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start space-x-3">
              <Bell className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900 dark:text-yellow-300">Önemli Notlar</h4>
                <ul className="text-sm text-yellow-800 dark:text-yellow-400 mt-2 space-y-1 list-disc list-inside">
                  <li>Form gönderimlerinde spam koruması aktiftir (IP başına 5 dakikada 3 gönderim)</li>
                  <li>Tüm form verileri güvenli bir şekilde şifrelenerek iletilir</li>
                  <li>Gönderilen formlar otomatik olarak "Yeni" statüsünde kaydedilir</li>
                  <li>Form verilerini kolayca lead'e dönüştürebilirsiniz</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Özelleştirme Seçenekleri</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-gray-700 dark:text-gray-300">Genişlik</span>
                <code className="text-blue-600 dark:text-blue-400">width="100%"</code>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-gray-700 dark:text-gray-300">Yükseklik</span>
                <code className="text-blue-600 dark:text-blue-400">height="700"</code>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-gray-700 dark:text-gray-300">Kenarlık Radius</span>
                <code className="text-blue-600 dark:text-blue-400">border-radius: 12px</code>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Bu değerleri iframe kodunda istediğiniz gibi düzenleyebilirsiniz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );



  const renderSystemSettings = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Sistem Ayarları</h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">Otomatik Yedekleme</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Verilerin otomatik olarak yedeklenmesi</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">Sistem Logları</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Detaylı sistem loglarını tut</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const [smsTemplate, setSmsTemplate] = useState('');
  const [isSavingSms, setIsSavingSms] = useState(false);

  useEffect(() => {
    if (activeTab === 'lead_sms') {
      const fetchSmsTemplate = async () => {
        const branchId = user?.branchId;
        if (!branchId) return;

        try {
          const { data: branch, error } = await supabase
            .from('branches')
            .select('welcome_sms_template')
            .eq('id', branchId)
            .single();

          if (error) throw error;

          if (branch?.welcome_sms_template) {
            setSmsTemplate(branch.welcome_sms_template);
          } else {
            setSmsTemplate('');
          }
        } catch (error) {
          console.error('Error fetching SMS template:', error);
        }
      };

      fetchSmsTemplate();
    }
  }, [activeTab, user?.branchId]);

  const handleSaveSmsTemplate = async () => {
    if (!smsTemplate.trim()) {
      alert('Lütfen bir SMS metni giriniz.');
      return;
    }

    setIsSavingSms(true);
    try {
      const branchId = user?.branchId;

      if (!branchId) {
        throw new Error('Şube bilginiz bulunamadı. Lütfen yöneticinizle iletişime geçin.');
      }

      const { error } = await supabase
        .from('branches')
        .update({ welcome_sms_template: smsTemplate })
        .eq('id', branchId);

      if (error) throw error;

      alert('SMS şablonu başarıyla kaydedildi.');
    } catch (error: any) {
      console.error('Error saving SMS template:', error);
      alert('Hata oluştu: ' + (error.message || 'Bilinmeyen bir hata oluştu. Veritabanı izni (RLS) eksik olabilir.'));
    } finally {
      setIsSavingSms(false);
    }
  };

  const renderLeadSmsSettings = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Lead SMS Ayarı (Şube Bazlı)</h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Bu şubeye düşen yeni lead'lere gönderilecek otomatik SMS metnini buradan düzenleyebilirsiniz.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Hoşgeldin SMS Metni
            </label>
            <textarea
              value={smsTemplate}
              onChange={(e) => setSmsTemplate(e.target.value)}
              rows={6}
              className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Merhaba, Pisa Eğitim ile iletişime geçtiğiniz için teşekkür ederiz..."
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              SMS içeriğinde özel karakterler kullanmamaya özen gösteriniz.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSaveSmsTemplate}
              disabled={isSavingSms}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{isSavingSms ? 'Kaydediliyor...' : 'Kaydet'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'profile': return renderProfileSettings();
      case 'notifications': return renderNotificationSettings();
      case 'security': return renderSecuritySettings();
      case 'system': return renderSystemSettings();
      case 'widget': return renderWidgetSettings();
      case 'lead_sms': return renderLeadSmsSettings();
      default: return renderProfileSettings();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Ayarlar</h2>
        <p className="text-gray-600 dark:text-gray-400">Sistem ve hesap ayarlarınızı yönetin</p>
      </div>

      {/* Settings Navigation */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-1">
        <nav className="flex space-x-1">
          {settingsTabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors ${activeTab === tab.id
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
              >
                <IconComponent className="w-5 h-5" />
                <span className="font-medium">{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Settings Content */}
      {renderContent()}
    </div>
  );
};

export default Settings;